import { TransformMode } from "./TransformMode";
import { TransformModeInject } from "./TransformModeInject";

export class Transform {
    public element: HTMLElement = null;
    public sceneTransform: DOMMatrix = null;
    public inverseSceneTransform: DOMMatrix = null;
    public mode: TransformMode = null;

    constructor(element: HTMLElement, mode?: TransformMode) {
        this.update(element, mode);
    }

    public static create(
        element?: HTMLElement,
        mode?: TransformMode
    ): Transform {
        return new Transform(element, mode);
    }

    public update(element: HTMLElement, mode?: TransformMode): Transform {
        this.element = element;
        this.mode = mode || TransformModeInject;

        if (this.element) {
            this.sceneTransform = this.mode.extract(this.element);
            this.inverseSceneTransform = this.sceneTransform.inverse();
        } else {
            this.sceneTransform = null;
            this.inverseSceneTransform = null;
        }

        return this;
    }

    public toLocal(point: DOMPoint): DOMPoint {
        // return point.matrixTransform(this.inverseSceneTransform);
        return this.unproject(point, this.inverseSceneTransform);
    }

    public static toLocal(
        element: HTMLElement,
        point: DOMPoint,
        mode?: TransformMode
    ): DOMPoint {
        return Transform.create(element, mode).toLocal(point);
    }

    public toLocalDelta(point: DOMPoint): DOMPoint {
        return this.deltaTransform(point, this.inverseSceneTransform);
    }

    public static toLocalDelta(
        element: HTMLElement,
        point: DOMPoint,
        mode?: TransformMode
    ): DOMPoint {
        return Transform.create(element, mode).toLocalDelta(point);
    }

    public toGlobalDelta(point: DOMPoint): DOMPoint {
        return this.deltaTransform(point, this.sceneTransform);
    }

    public static toGlobalDelta(
        element: HTMLElement,
        point: DOMPoint,
        mode?: TransformMode
    ): DOMPoint {
        return Transform.create(element, mode).toGlobalDelta(point);
    }

    private deltaTransform(point: DOMPoint, matrix: DOMMatrix): DOMPoint {
        var x =
            point.x * matrix.m11 + point.y * matrix.m21 + point.z * matrix.m31;
        var y =
            point.x * matrix.m12 + point.y * matrix.m22 + point.z * matrix.m32;
        var z =
            point.x * matrix.m13 + point.y * matrix.m23 + point.z * matrix.m33;
        var w =
            point.x * matrix.m14 + point.y * matrix.m24 + point.z * matrix.m34;
        return new DOMPoint(x, y, z, w);
    }

    public toGlobal(point: DOMPoint): DOMPoint {
        // return point.matrixTransform(this.sceneTransform);
        return this.project(point, this.sceneTransform);
    }

    public static toGlobal(
        element: HTMLElement,
        point: DOMPoint,
        mode?: TransformMode
    ): DOMPoint {
        return Transform.create(element, mode).toGlobal(point);
    }

    public localToLocal(to: Transform, point: DOMPoint): DOMPoint {
        const global = this.toGlobal(point);
        return to.toLocal(global);
    }

    /**
     * From homegeneous (euclid) to cartesian FLATTENED!!!! like a projection
     *
     * @param point
     * @param matrix
     * @returns
     */
    private project(point: DOMPoint, matrix: DOMMatrix): DOMPoint {
        var z = point.z;
        var w =
            point.x * matrix.m14 +
            point.y * matrix.m24 +
            z * matrix.m34 +
            matrix.m44;
        var x =
            point.x * matrix.m11 +
            point.y * matrix.m21 +
            z * matrix.m31 +
            matrix.m41;
        var y =
            point.x * matrix.m12 +
            point.y * matrix.m22 +
            z * matrix.m32 +
            matrix.m42;

        if (w == 0) w = 0.0001;

        x /= w;
        y /= w;

        //lets call it "hasenfuss"
        //look at the developer tools of firefox and chrome ->
        //ff and chrome do it wrong: the highlighted bounding box failed to be correct
        //and getBoundingClientRect also

        //behind the "camera" (z > 0)
        if (w < 0) {
            x -= matrix.m41;
            y -= matrix.m42;
            x *= 1 / w;
            y *= 1 / w;
            x += matrix.m41;
            y += matrix.m42;
        }

        return new DOMPoint(x, y, z, w);
    }

    /**
     * based on http://code.metager.de/source/xref/mozilla/B2G/gecko/gfx/thebes/gfx3DMatrix.cpp#651
     *
     * @param point
     * @param matrix
     * @returns
     */
    private unproject(point: DOMPoint, matrix: DOMMatrix): DOMPoint {
        var x = point.x * matrix.m11 + point.y * matrix.m21 + matrix.m41;
        var y = point.x * matrix.m12 + point.y * matrix.m22 + matrix.m42;
        var z = point.x * matrix.m13 + point.y * matrix.m23 + matrix.m43;
        var w = point.x * matrix.m14 + point.y * matrix.m24 + matrix.m44;

        var qx = x + matrix.m31;
        var qy = y + matrix.m32;
        var qz = z + matrix.m33;
        var qw = w + matrix.m34;

        if (w == 0) w = 0.0001;
        x /= w;
        y /= w;
        z /= w;

        if (qw == 0) qw = 0.0001;
        qx /= qw;
        qy /= qw;
        qz /= qw;

        //TODO: ....
        var wz = qz - z;
        if (wz == 0) {
            return new DOMPoint(x, y, z, w);
        }

        var t = -z / wz;
        x += t * (qx - x);
        y += t * (qy - y);

        return new DOMPoint(x, y, z, w);
    }
}
